import { computed, effect, Injectable, signal } from "@angular/core";

export type Message = {
  title: string;
  text: string;
  position?: 'above' | 'below' | 'left' | 'right';
  query?: string;
  skipWhenQuerriedClicked?: boolean;
  hideNextButton?: boolean;
};

@Injectable()
export class OnboardingTourService {

  storageID = signal<string|null>(null);
  messages = signal<Message[]>([]);
  currentMessageIndex = signal<number>(-1);
  tourCompleted = computed(() => this.messages().length > 0 && this.currentMessageIndex() >= this.messages().length);

  currentMessage = computed(() => {
    if (this.messages().length > 0) {
      if (this.currentMessageIndex() >= 0 && this.currentMessageIndex() < this.messages().length) {
        return this.messages()[this.currentMessageIndex()];
      }
    }
    return null;
  });

  constructor() {
    effect(() => {
      const id = this.storageID();
      if (id !== null) {
        const stored = localStorage.getItem(`onboarding-tour-${id}`);
        if (stored) {
          const object = JSON.parse(stored);
          if ('currentMessageIndex' in object) {
            this.currentMessageIndex.set(object.currentMessageIndex);
          }
        }
      }
    });
  }

  /**
   * Set the current tour and override the previous tour if one exists. Only one tour is displayed at a time.
   * @param storageID Key under which to store the tour progress in localstorage.
   * @param messages List of messages the tour consists of.
   */
  setTour(storageID: string, messages: Message[]) {
    this.messages.set(messages);
    this.storageID.set(storageID);
  }

  /**
   * Start the tour. Displays the first message if there are any messages.
   */
  startTour() {
    this.currentMessageIndex.set(0);
  }

  /**
   * Stop the tour as if it had not started yet.
   */
  stopTour() {
    this.currentMessageIndex.set(-1);
  }

  /**
   * Skip to the end of the tour. This makes the onboarding tour disappear.
   */
  skipEntireTour() {
    if (this.messages().length > 0) {
      this.currentMessageIndex.set(this.messages().length);
      this.save();
    }
  }

  /**
   * Move on to the next message in the tour, or end the tour if it has no more messages.
   */
  next() {
    if (this.messages().length > 0 && this.currentMessageIndex() < this.messages().length) {
      this.currentMessageIndex.update(index => index + 1);
      this.save();
    }
  }

  /**
   * Return to the previous message until the start of the tour is hit. This method cannot end the tour.
   */
  previous() {
    if (this.messages().length > 0 && this.currentMessageIndex() > 0) {
      this.currentMessageIndex.update(index => index + -1);
      this.save();
    }
  }

  private save() {
    const id = this.storageID();
    if (id !== null) {
      localStorage.setItem(`onboarding-tour-${id}`, JSON.stringify({
        currentMessageIndex: this.currentMessageIndex(),
      }));
    }
  }
}
